/** The Stores Arrival lists: one pill per arrival type, one tab per status. */
import { STORES_ARRIVAL_GROUPS, STORES_DOC_TABS } from "@nexseed/shared";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { ListPageSkeleton } from "@/components/app/skeletons";
import { useAuth } from "@/features/auth/store";
import { useFacilities } from "@/features/auth/use-facilities";
import { PackagePlus } from "lucide-react";
import { ArrivalListPage } from "@/features/stores/components/arrival-list-page";
import { ChooserGate } from "@/features/stores/components/type-chooser";
import { ARRIVAL_TYPE_OPTIONS } from "@/features/stores/type-options";

const search = z.object({
  group: z
    .enum(
      STORES_ARRIVAL_GROUPS.map((g) => g.value) as [
        "vendor",
        "stock_transfer",
        "return",
      ]
    )
    // No default: the Stores menu lands here with no type and the pop-up asks (user, 2026-10-08).
    .optional(),
  tab: z.enum([...STORES_DOC_TABS, "waiting"]).default("submitted"),
  from: z.string().optional(),
  to: z.string().optional(),
  q: z.string().optional(),
  page: z.number().int().min(1).default(1),
  view: z.enum(["transit"]).optional(),
});

export const Route = createFileRoute("/_app/stores/arrivals/")({
  validateSearch: search,
  beforeLoad: () => {
    if (!useAuth.getState().can("stores.view")) throw redirect({ to: "/" });
  },
  component: Page,
});

function Page() {
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { selected, ready } = useFacilities();
  if (!ready) return <ListPageSkeleton />;
  const { group } = s;
  if (!group)
    return (
      <ChooserGate
        pageTitle="Stores Arrival"
        breadcrumbs={[{ label: "Stores" }, { label: "Arrival" }]}
        chooserTitle="Transaction: Arrival Types"
        chooserDescription="Where are the items coming from?"
        options={ARRIVAL_TYPE_OPTIONS}
        icon={PackagePlus}
        onPick={(g) =>
          void navigate({ search: (prev) => ({ ...prev, group: g, page: 1 }) })
        }
      />
    );
  return (
    <ArrivalListPage
      facilityId={selected?.id}
      search={{ ...s, group }}
      onSearch={(next) =>
        void navigate({ search: (prev) => ({ ...prev, ...next }) })
      }
      onChangeType={() =>
        void navigate({
          search: (prev) => ({ ...prev, group: undefined, view: undefined }),
        })
      }
    />
  );
}
